// Multi-field search box with match-mode toggle, shared by the stock ledger
// and the movement window so both screens search the same way.
//
// Mirrors the legacy stock-ledger "Starts With" / "Similar Articles"
// checkboxes, collapsed into one segmented control:
//   contains   — substring (default)
//   startswith — anchored at start of field
//   similar    — strip spaces / - / . / and prefix-match the normalised form,
//                so "SM119" finds "SM 119", "SM-119", "SM 119 / B"
//
// Typing is debounced here (250ms) so callers can push `q` straight into the
// URL without thrashing history or refetching on every keystroke.

import * as React from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import type { MatchMode } from '@/lib/inventory-api';
import { cn } from '@/lib/utils';

const MATCH_MODES: ReadonlyArray<{ value: MatchMode; label: string; hint: string }> = [
  { value: 'contains', label: 'Contains', hint: 'Substring match (default)' },
  { value: 'startswith', label: 'Starts with', hint: 'Match anchored at start of field' },
  { value: 'similar', label: 'Similar', hint: 'Ignore spaces / hyphens / slashes' },
];

function MatchModeToggle({
  value, onChange,
}: { value: MatchMode; onChange: (m: MatchMode) => void }) {
  return (
    <div
      role="radiogroup"
      aria-label="Search match mode"
      className="flex h-10 items-center rounded-md border bg-background p-0.5"
    >
      {MATCH_MODES.map((m) => {
        const selected = value === m.value;
        return (
          <button
            key={m.value}
            type="button"
            role="radio"
            aria-checked={selected}
            title={m.hint}
            onClick={() => onChange(m.value)}
            className={cn(
              'h-full rounded px-3 text-xs font-medium transition-colors',
              selected ? 'bg-ink text-white shadow-sm' : 'text-muted-foreground hover:text-ink'
            )}
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

function matchPlaceholder(m: MatchMode): string {
  switch (m) {
    case 'startswith': return 'Starts with… e.g. SM 119';
    case 'similar': return 'Similar… e.g. SM119 finds SM 119, SM-119/B';
    default: return 'Brand, item, article, color…';
  }
}

export function SearchBox({
  q, onQChange, match, onMatchChange, label = 'Search', placeholder, className,
}: {
  q: string | null;
  onQChange: (q: string | undefined) => void;
  match: MatchMode;
  onMatchChange: (m: MatchMode) => void;
  label?: string;
  placeholder?: string;
  className?: string;
}) {
  const [draft, setDraft] = React.useState(q ?? '');
  React.useEffect(() => setDraft(q ?? ''), [q]);
  React.useEffect(() => {
    const t = setTimeout(() => {
      const next = draft.trim();
      if (next !== (q ?? '')) onQChange(next || undefined);
    }, 250);
    return () => clearTimeout(t);
  }, [draft, q, onQChange]);

  return (
    <div className={cn('min-w-[220px] flex-1', className)}>
      <label className="mb-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        <Search className="h-3.5 w-3.5" /> {label}
      </label>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder ?? matchPlaceholder(match)}
            className="h-10 pr-8"
          />
          {draft && (
            <button
              type="button"
              onClick={() => setDraft('')}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-ink"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <MatchModeToggle value={match} onChange={onMatchChange} />
      </div>
    </div>
  );
}
